import { RoleKey } from './enums.js';
/**
 * Permission catalogue.
 *
 * Every protected API route names one of these, and the web client
 * gates navigation on the same list, so the UI can never offer an action the
 * server will refuse.
 *
 * Format is `resource:action`.
 */
export const PERMISSIONS = [
    // Organisation & administration
    'tenant:manage',
    'company:read',
    'company:create',
    'company:update',
    'company:delete',
    'group:read',
    'group:create',
    'group:update',
    'group:delete',
    'region:read',
    'region:create',
    'region:update',
    'region:delete',
    'vertical:read',
    'vertical:create',
    'vertical:update',
    'vertical:delete',
    'business_unit:read',
    'business_unit:create',
    'business_unit:update',
    'business_unit:delete',
    'location:read',
    'location:create',
    'location:update',
    'location:delete',
    'department:read',
    'department:create',
    'department:update',
    'department:delete',
    'user:read',
    'user:create',
    'user:update',
    'user:delete',
    'role:read',
    'role:create',
    'role:update',
    'role:delete',
    // Vendor master & company payment accounts (bank accounts, cards, net banking)
    'vendor:read',
    'vendor:create',
    'vendor:update',
    'vendor:delete',
    // The Payment Accounts section: cards, net banking logins and the account
    // details behind them. `bank_account:read` alone only lists accounts to pick
    // from, in a payment batch or a statement upload.
    'payment_account:read',
    'bank_account:read',
    'bank_account:create',
    'bank_account:update',
    'bank_account:delete',
    // Vendor advance balance. `create` records a deposit or top-up paid outside
    // the platform; `reverse` undoes a mistaken entry and is a manager's call.
    // Using the balance against an invoice is part of `invoice:verify`.
    'vendor_advance:read',
    'vendor_advance:create',
    'vendor_advance:reverse',
    // General ledgers: each company's chart of accounts, including vendors' own
    // accounts (payable, advance, deposit). Invoices are tagged against them at
    // accounting verification; nothing is posted — the books stay in the ERP.
    'ledger:read',
    'ledger:create',
    'ledger:update',
    'ledger:delete',
    // The tenant's TDS sections and the rates each is deducted at, shared by all
    // its companies; invoices and vendor profiles pick from them.
    'tds_section:read',
    'tds_section:create',
    'tds_section:update',
    'tds_section:delete',
    // Loans the company has taken: entered, activated and tracked under
    // Payment Accounts; each EMI becomes a payment obligation.
    'loan:read',
    'loan:create',
    'loan:update',
    'loan:delete',
    // Invoices
    'invoice:read',
    'invoice:create',
    'invoice:update',
    'invoice:submit',
    'invoice:resolve_duplicate',
    'invoice:cancel',
    'invoice:delete',
    'invoice:approve',
    // The accounting team's stage: verify an approved invoice, set TDS and
    // release it to accounts payable. Distinct from `invoice:approve`, which is
    // business sign-off.
    'invoice:verify',
    // Mailbox connectors — a user's own Outlook, connected to pull invoices.
    // `manage` is deliberately one permission rather than a create/update/delete
    // set: there is exactly one connection per user and every action on it is the
    // same act of owning your own mailbox.
    'mail_connection:manage',
    'mail_connection:read_all',
    // Approvals
    'approval_rule:read',
    'approval_rule:create',
    'approval_rule:update',
    'approval_rule:delete',
    'approval:read',
    'approval:read_all',
    // Treasury escalation. `act` is the decision (approve / reject / return);
    // `create` is finance raising the request in the first place.
    'finance_request:read',
    'finance_request:read_all',
    'finance_request:create',
    'finance_request:act',
    // Accounts payable
    'payable:read',
    // Payroll — deliberately separate from every other finance permission,
    // because salary data must not be visible to ordinary AP users (PRD §18).
    'payroll:read',
    'payroll:create',
    'payroll:update',
    'payroll:submit',
    'payroll:delete',
    'payroll:approve',
    // The employee master, kept by HR. Read by payroll and oversight roles only:
    // like salary data, it is not for the rest of finance.
    'employee:read',
    'employee:create',
    'employee:update',
    // Payment pipeline
    'obligation:read',
    'obligation:update',
    'payment_batch:read',
    'payment_batch:create',
    'payment_batch:update',
    'payment_batch:export',
    'payment_batch:delete',
    // Banking
    'bank_statement:read',
    'bank_statement:create',
    'bank_statement:delete',
    'bank_transaction:read',
    // Reconciliation
    'reconciliation:read',
    'reconciliation:match',
    'reconciliation:confirm',
    'reconciliation:ignore',
    // Insight
    'report:read',
    'report:export',
    'audit:read',
    'dashboard:read',
    'notification:read',
    // Vendor portal — a vendor's own login, reading the invoices it billed.
    // Scoped by the user's linked vendor rows, never by organisation, so no
    // internal role holds it: a finance user has the real invoice register.
    'vendor_portal:read',
];
/** The `resource` half of a `resource:action` permission. */
export function permissionResource(permission) {
    return permission.slice(0, permission.indexOf(':'));
}
/** The `action` half of a `resource:action` permission. */
export function permissionAction(permission) {
    return permission.slice(permission.indexOf(':') + 1);
}
/** Human names for each resource, used to head the permission picker. */
export const PERMISSION_RESOURCE_LABELS = {
    tenant: 'Tenant',
    company: 'Companies',
    group: 'Groups',
    region: 'Regions',
    vertical: 'Verticals',
    business_unit: 'Business units',
    location: 'Locations',
    department: 'Departments',
    user: 'Users',
    role: 'Roles',
    vendor: 'Vendors',
    vendor_advance: 'Vendor advances',
    ledger: 'General ledgers',
    tds_section: 'TDS sections',
    loan: 'Loans',
    payment_account: 'Payment Accounts section (cards, net banking)',
    bank_account: 'Bank accounts',
    invoice: 'Invoices',
    mail_connection: 'Mailbox connectors',
    approval_rule: 'Approval rules',
    approval: 'Approvals',
    finance_request: 'Treasury requests',
    payable: 'Accounts payable',
    payroll: 'Payroll',
    employee: 'Employees',
    obligation: 'Payment obligations',
    payment_batch: 'Payment batches',
    bank_statement: 'Bank statements',
    bank_transaction: 'Bank transactions',
    reconciliation: 'Reconciliation',
    report: 'Reports',
    audit: 'Audit trail',
    dashboard: 'Dashboard',
    notification: 'Notifications',
    vendor_portal: 'Vendor portal',
};
/**
 * The catalogue arranged by resource, in declaration order.
 *
 * The role editor renders one checkbox group per entry, so this is what keeps
 * the picker and the enforced catalogue from drifting apart: a permission
 * added above shows up in the interface without further work.
 */
export const PERMISSION_GROUPS = buildPermissionGroups();
function buildPermissionGroups() {
    const groups = new Map();
    for (const permission of PERMISSIONS) {
        const resource = permissionResource(permission);
        const group = groups.get(resource) ?? {
            resource,
            label: PERMISSION_RESOURCE_LABELS[resource] ?? resource.replace(/_/g, ' '),
            permissions: [],
        };
        group.permissions.push(permission);
        groups.set(resource, group);
    }
    return [...groups.values()];
}
const PERMISSION_SET = new Set(PERMISSIONS);
/** Narrows an arbitrary string — a request body, a stored role — to a known permission. */
export function isPermission(value) {
    return PERMISSION_SET.has(value);
}
/**
 * The vendor portal is an outside party's view, filtered by the vendor rows a
 * login is linked to. An internal user has no such link, so the permission
 * would only ever show them an empty page; it is held by the Vendor role alone.
 */
const VENDOR_ONLY = ['vendor_portal:read'];
/**
 * The Payment Accounts section, loans included, is Treasury's among the
 * built-in roles, Platform Admin and Auditor included. Anyone else needs a
 * custom role that grants it. `bank_account:read` is not part of this: finance
 * picks an account from it to pay from.
 */
const PAYMENT_ACCOUNTS = [
    'payment_account:read',
    'bank_account:create',
    'bank_account:update',
    'bank_account:delete',
    'loan:read',
    'loan:create',
    'loan:update',
    'loan:delete',
];
const ALL = PERMISSIONS.filter((p) => !VENDOR_ONLY.includes(p) && !PAYMENT_ACCOUNTS.includes(p));
const READ_ONLY = ALL.filter((p) => (p.endsWith(':read') || p.endsWith(':read_all') || p === 'report:export') &&
    // Oversight of colleagues' mailboxes is a deliberate grant, not something a
    // read-only role picks up from the `:read_all` suffix. An auditor reading
    // the subjects and senders of personal mail is a privacy decision, so it is
    // made explicitly per role rather than by naming convention.
    p !== 'mail_connection:read_all');
/**
 * Every organisation level, read-only.
 *
 * Held by anyone who can filter or file work by them — which is everyone with
 * an operational screen, since the scope bar offers these axes. Managing them
 * is a separate, admin-only set.
 */
const ORG_READ = [
    'company:read',
    'group:read',
    'region:read',
    'vertical:read',
    'business_unit:read',
    'location:read',
    'department:read',
];
/** The same levels, fully managed. Company Admin and above only. */
const ORG_MANAGE = [
    ...ORG_READ,
    'company:create',
    'company:update',
    'company:delete',
    'group:create',
    'group:update',
    'group:delete',
    'region:create',
    'region:update',
    'region:delete',
    'vertical:create',
    'vertical:update',
    'vertical:delete',
    'business_unit:create',
    'business_unit:update',
    'business_unit:delete',
    'location:create',
    'location:update',
    'location:delete',
    'department:create',
    'department:update',
    'department:delete',
];
/**
 * Finance Executive (PRD §7): prepares work but never approves it and never
 * sees payroll. Explicitly excludes `invoice:approve` and every `payroll:*`.
 */
const FINANCE_EXECUTIVE = [
    ...ORG_READ,
    'vendor:read',
    'vendor:create',
    'vendor:update',
    // Whoever sets a vendor up records the advance paid to it.
    'vendor_advance:read',
    'vendor_advance:create',
    'ledger:read',
    'tds_section:read',
    'bank_account:read',
    'invoice:read',
    'invoice:create',
    'invoice:update',
    'invoice:submit',
    'invoice:resolve_duplicate',
    // Matches `invoice:create`: whoever may create an invoice may connect their
    // own Outlook to pull them.
    'mail_connection:manage',
    'approval:read',
    'payable:read',
    'obligation:read',
    'payment_batch:read',
    'payment_batch:create',
    'payment_batch:update',
    'payment_batch:export',
    'bank_statement:read',
    'bank_statement:create',
    'bank_transaction:read',
    'reconciliation:read',
    'reconciliation:match',
    'reconciliation:confirm',
    'reconciliation:ignore',
    'report:read',
    'report:export',
    'dashboard:read',
    'notification:read',
];
const FINANCE_MANAGER = [
    ...FINANCE_EXECUTIVE,
    'invoice:approve',
    'mail_connection:read_all',
    'invoice:cancel',
    // Delete an invoice someone else uploaded, while it is still before submission.
    'invoice:delete',
    'vendor_advance:reverse',
    'ledger:create',
    'ledger:update',
    'ledger:delete',
    'tds_section:create',
    'tds_section:update',
    'tds_section:delete',
    'approval:read_all',
    // The finance head is who escalates to Treasury, and follows the outcome.
    'finance_request:create',
    'finance_request:read',
    'finance_request:read_all',
    'approval_rule:read',
    'obligation:update',
    'payment_batch:delete',
    'bank_statement:delete',
    'audit:read',
    'user:read',
];
const APPROVER = [
    ...ORG_READ,
    'vendor:read',
    'invoice:read',
    'invoice:approve',
    'approval:read',
    'payable:read',
    'dashboard:read',
    'notification:read',
    'report:read',
];
/**
 * Vertical Head: the first business approver in the revised ladder. An
 * Approver whose remit is a vertical rather than a department — the difference
 * is enforced by the user's vertical scope, not by extra permissions.
 */
const VERTICAL_HEAD = [...APPROVER];
/**
 * Accounting Team: verifies business-approved invoices, sets TDS and either
 * releases to accounts payable or escalates to Treasury.
 *
 * Deliberately not an approver — it holds `invoice:verify`, not
 * `invoice:approve`, so the accounting stage cannot substitute for business
 * sign-off. No payroll, like every other AP-side role.
 */
const ACCOUNTS_TEAM = [
    ...ORG_READ,
    'vendor:read',
    'vendor:update',
    // Sees the balance it draws on when verifying an invoice.
    'vendor_advance:read',
    // Tags each invoice with ledgers from the chart when verifying it.
    'ledger:read',
    'tds_section:read',
    'invoice:read',
    'invoice:update',
    'invoice:verify',
    'approval:read_all',
    'finance_request:read',
    'finance_request:create',
    'payable:read',
    'obligation:read',
    'payment_batch:read',
    'bank_account:read',
    'bank_statement:read',
    'bank_transaction:read',
    'reconciliation:read',
    'report:read',
    'report:export',
    'audit:read',
    'dashboard:read',
    'notification:read',
];
/**
 * Treasury: decides escalated finance requests and owns Payment Accounts.
 * Read-only everywhere else, including the bank's statements and transactions —
 * Treasury sees the position and the evidence.
 */
const TREASURY = [
    ...PAYMENT_ACCOUNTS,
    ...ORG_READ,
    'vendor:read',
    'vendor_advance:read',
    'ledger:read',
    'tds_section:read',
    'invoice:read',
    'approval:read_all',
    'finance_request:read',
    'finance_request:read_all',
    'finance_request:act',
    'payable:read',
    'obligation:read',
    'payment_batch:read',
    'bank_account:read',
    'bank_statement:read',
    'bank_transaction:read',
    'reconciliation:read',
    'report:read',
    'report:export',
    'audit:read',
    'dashboard:read',
    'notification:read',
];
const CFO = [
    ...FINANCE_MANAGER,
    'payroll:read',
    'payroll:approve',
    'employee:read',
    'approval:read_all',
    'company:read',
    'audit:read',
    'approval_rule:read',
];
const PAYROLL_USER = [
    ...ORG_READ,
    'employee:read',
    'payroll:read',
    'payroll:create',
    'payroll:update',
    'payroll:submit',
    'payroll:delete',
    'approval:read',
    'dashboard:read',
    'notification:read',
    'report:read',
    'report:export',
];
const COMPANY_ADMIN = [
    ...ORG_MANAGE,
    // Sees the employee master; HR keeps it.
    'employee:read',
    'user:read',
    'user:create',
    'user:update',
    'user:delete',
    'role:read',
    'role:create',
    'role:update',
    'role:delete',
    'vendor:read',
    'vendor:create',
    'vendor:update',
    'vendor:delete',
    'vendor_advance:read',
    'vendor_advance:create',
    'vendor_advance:reverse',
    'ledger:read',
    'ledger:create',
    'ledger:update',
    'ledger:delete',
    'tds_section:read',
    'tds_section:create',
    'tds_section:update',
    'tds_section:delete',
    'bank_account:read',
    'approval_rule:read',
    'approval_rule:create',
    'approval_rule:update',
    'approval_rule:delete',
    'approval:read_all',
    // Oversight only — a company admin watches Treasury requests, never decides
    // one, the same way it watches approvals without approving.
    'finance_request:read_all',
    'invoice:read',
    // Oversight only. A company admin cannot create invoices, so it never gets
    // `mail_connection:manage` — it can watch the connectors, not run them.
    'mail_connection:read_all',
    'payable:read',
    'obligation:read',
    'payment_batch:read',
    'bank_statement:read',
    'bank_transaction:read',
    'reconciliation:read',
    'report:read',
    'report:export',
    'audit:read',
    'dashboard:read',
    'notification:read',
];
const AUDITOR = [...new Set([...READ_ONLY, 'audit:read'])];
/**
 * HR keeps the employee master: adds, edits and imports employees and moves
 * them through their lifecycle. It sees payroll, read-only — importing,
 * submitting and approving it stay with payroll and the CFO — and nothing of
 * invoices or payments, so payroll stays apart from AP.
 */
const HR = [
    ...ORG_READ,
    'employee:read',
    'employee:create',
    'employee:update',
    'payroll:read',
    'dashboard:read',
    'notification:read',
];
/** Role → permission grants. Deduplicated and frozen. */
export const ROLE_PERMISSIONS = {
    [RoleKey.PLATFORM_ADMIN]: ALL,
    [RoleKey.COMPANY_ADMIN]: dedupe(COMPANY_ADMIN),
    [RoleKey.FINANCE_EXECUTIVE]: dedupe(FINANCE_EXECUTIVE),
    [RoleKey.FINANCE_MANAGER]: dedupe(FINANCE_MANAGER),
    [RoleKey.APPROVER]: dedupe(APPROVER),
    [RoleKey.VERTICAL_HEAD]: dedupe(VERTICAL_HEAD),
    [RoleKey.ACCOUNTS_TEAM]: dedupe(ACCOUNTS_TEAM),
    [RoleKey.TREASURY]: dedupe(TREASURY),
    [RoleKey.CFO]: dedupe(CFO),
    [RoleKey.PAYROLL_USER]: dedupe(PAYROLL_USER),
    [RoleKey.AUDITOR]: dedupe(AUDITOR),
    [RoleKey.HR]: dedupe(HR),
    [RoleKey.VENDOR]: dedupe(VENDOR_ONLY),
};
function dedupe(list) {
    return [...new Set(list)];
}
/** True when the key names one of the roles built into the product. */
export function isSystemRoleKey(key) {
    return key in ROLE_PERMISSIONS;
}
/**
 * The permissions a user's one role grants.
 *
 * A user holds exactly one role; a combination of duties is a role of its own
 * rather than two roles unioned at request time. `customGrants` carries the
 * tenant's own roles, which are rows rather than code; a key found in neither
 * map grants nothing, so a role deleted out from under a user silently narrows
 * their access instead of widening it.
 */
export function permissionsForRole(roleKey, customGrants) {
    if (!roleKey)
        return [];
    const fromRole = isSystemRoleKey(roleKey)
        ? ROLE_PERMISSIONS[roleKey]
        : (customGrants?.[roleKey] ?? []);
    return [...new Set(fromRole)];
}
export function hasPermission(granted, required) {
    return !!granted?.includes(required);
}
export function hasAnyPermission(granted, required) {
    return required.some((permission) => hasPermission(granted, permission));
}
export function hasAllPermissions(granted, required) {
    return required.every((permission) => hasPermission(granted, permission));
}
/**
 * Roles allowed to see individual salary figures. Used by the payment queue to
 * decide whether payroll obligations are itemised or shown as one aggregate
 * line (PRD §18, §21).
 */
export const PAYROLL_VISIBILITY_PERMISSION = 'payroll:read';
//# sourceMappingURL=permissions.js.map